import { useMemo, useState } from 'react';

import { useQuery, useQueryClient } from '@tanstack/react-query';
import { DownloadIcon, PrinterIcon } from 'lucide-react';

import { ApiError } from '@mds/shared/lib/api';
import {
  answerInputs,
  blankAnswer,
  carryAnswers,
  questionsOf,
  type AnswerDraft,
  type AnswerDrafts,
} from '@mds/shared/lib/form-answers';
import type { AdminEvent } from '@mds/shared/types/event';
import type {
  AdminEventRegistration,
  EventPaymentMethod,
} from '@mds/shared/types/event-registration';
import type { FormQuestion, FormQuestionSet } from '@mds/shared/types/form-question';
import useDebounced from '@mds/shared/hooks/use-debounced';
import Select from '@mds/shared/ui/select';

import { useSession } from '@/auth/use-session';
import QuestionField from '@/components/events/question-field';
import Button from '@/components/ui/button';
import Card from '@/components/ui/card';
import Chip from '@/components/ui/chip';
import EmptyState from '@/components/ui/empty-state';
import Field, { controlCls, inputCls, selectCls } from '@/components/ui/field';
import Modal from '@/components/ui/modal';
import Pager from '@/components/ui/pager';
import PrintFrame from '@/components/reporting/print-frame';
import StatCard from '@/components/ui/stat-card';
import { Table, Row, Cell } from '@/components/ui/table';
import { Toolbar } from '@/components/ui/toolbar';
import { eventPriceLabel } from '@/domain/events';
import { errorCode } from '@/domain/form-builder';
import {
  EVENT_PAYMENT_METHOD_OPTIONS,
  REGISTRATION_FORM_CHANGED,
  answerLine,
  arrivesWaitlisted,
  cancelRegistrationSchema,
  eventPaymentSchema,
  registrationFacts,
  registrationProblem,
  registrationSchema,
  registrationTone,
  seatsFree,
  seatsLabel,
} from '@/domain/registrations';
import { exportRegistrationXlsx } from '@/events/registration-export';
import * as registrationForm from '@/events/registration-form.api';
import * as registrations from '@/events/registrations.api';
import { stamp } from '@mds/shared/lib/dates';
import { printDialog } from '@/lib/export';
import { formatCents } from '@/lib/money';
import { useValidation, type FieldBinder } from '@/lib/validation';

const PAGE_SIZE = 25;

const STATUS_OPTIONS = [
  { value: 'all', label: 'Every status' },
  { value: 'confirmed', label: 'Confirmed' },
  { value: 'payment_pending', label: 'Payment due' },
  { value: 'waitlisted', label: 'Waitlist' },
  { value: 'cancelled', label: 'Cancelled' },
];

const ATTENDANCE_OPTIONS = [
  { value: 'all', label: 'Any attendance' },
  { value: 'checked_in', label: 'Checked in' },
  { value: 'pending', label: 'Not yet in' },
];

interface NewRegistration {
  attendeeName: string;
  attendeeEmail: string;
  attendeePhone: string;
  seatCount: string;
}

const blankRegistration: NewRegistration = {
  attendeeName: '',
  attendeeEmail: '',
  attendeePhone: '',
  seatCount: '1',
};

interface PaymentDraft {
  method: EventPaymentMethod;
  methodDetail: string;
  note: string;
}

const blankPayment: PaymentDraft = { method: 'cash', methodDetail: '', note: '' };

/** Asks nothing: what the add dialog validates against until the event's form has loaded. */
const NO_QUESTIONS: FormQuestionSet = { ungroupedQuestions: [], sections: [] };

/**
 * Who is coming to one event: the registration list, its figures, and every
 * action an administrator can take on a party — add one, take its full
 * payment, move it on or off the waitlist, cancel it, and tick it in or out at
 * the door.
 *
 * One registration is one party with one ticket, so seats are counted per row
 * and a check-in covers the whole party. Nobody is turned away for lack of
 * room: a party that does not fit joins the waitlist, and staff promote it when
 * seats free up.
 */
export default function AttendeesPanel({ event }: { event: AdminEvent }) {
  const queryClient = useQueryClient();
  const { can } = useSession();
  const mayManage = can('events.manage');
  const mayRecord = mayManage && can('payments.record');
  const mayCheckIn = can('events.checkin');
  const priced = event.price.amountMinor > 0;

  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const settled = useDebounced(search);
  const [status, setStatus] =
    useState<NonNullable<registrations.RegistrationQuery['status']>>('all');
  const [attendance, setAttendance] =
    useState<NonNullable<registrations.RegistrationQuery['attendance']>>('all');

  const [openId, setOpenId] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  const [draft, setDraft] = useState<NewRegistration>(blankRegistration);
  const [answers, setAnswers] = useState<AnswerDrafts>({});
  const [paying, setPaying] = useState(false);
  const [payment, setPayment] = useState<PaymentDraft>(blankPayment);
  const [cancelling, setCancelling] = useState(false);
  /** Confirming the cancellation of every live registration; shares `reason` with `cancelling`. */
  const [cancellingAll, setCancellingAll] = useState(false);
  const [reason, setReason] = useState('');
  /** Confirming a move off or onto the waitlist for the open registration. */
  const [moving, setMoving] = useState<'promote' | 'waitlist' | null>(null);
  const [busy, setBusy] = useState(false);
  const [togglingId, setTogglingId] = useState<string | null>(null);
  const [exporting, setExporting] = useState(false);
  const [exportProblem, setExportProblem] = useState<string | null>(null);
  const [problem, setProblem] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  // Staff answer the same registration form a guest does, so the dialog waits for it.
  const form = useQuery({
    queryKey: ['registration-form', event.id],
    queryFn: () => registrationForm.getForm(event.id),
    enabled: adding,
  });
  const questions = form.data ?? NO_QUESTIONS;
  const addSchema = useMemo(() => registrationSchema(event, questions), [event, questions]);
  const addDraft = useMemo(() => ({ ...draft, answers }), [draft, answers]);
  const addValidation = useValidation(addSchema, addDraft);
  const payValidation = useValidation(eventPaymentSchema, payment);
  const cancelDraft = useMemo(() => ({ reason }), [reason]);
  const cancelValidation = useValidation(cancelRegistrationSchema, cancelDraft);

  const list = useQuery({
    queryKey: ['registrations', event.id, { page, settled, status, attendance }],
    queryFn: () =>
      registrations.listRegistrations(event.id, {
        page,
        limit: PAGE_SIZE,
        // The API refuses a blank or overlong search rather than ignoring it.
        q: settled.trim().slice(0, 100) || undefined,
        status,
        attendance,
        // The waitlist reads in the order parties joined it; everything else newest first.
        ...(status === 'waitlisted'
          ? { sort: 'waitlistedAt' as const, direction: 'asc' as const }
          : { sort: 'createdAt' as const, direction: 'desc' as const }),
      }),
  });

  const single = useQuery({
    queryKey: ['registrations', event.id, 'one', openId],
    queryFn: () => registrations.getRegistration(event.id, openId as string),
    enabled: openId !== null,
  });
  const open = single.data ?? null;

  const rows = list.data?.data ?? [];
  const summary = list.data?.summary;
  /** Event-wide, whatever the filters. Only parties that have checked in are never cancelled. */
  const cancellableCount = summary
    ? summary.registrationCount -
      summary.cancelledRegistrationCount -
      summary.checkedInRegistrationCount
    : 0;

  /** Seats and status move on the event too, so both are re-read after any write. */
  const refresh = async () => {
    await queryClient.invalidateQueries({ queryKey: ['registrations', event.id] });
    await queryClient.invalidateQueries({ queryKey: ['event', event.id] });
    await queryClient.invalidateQueries({ queryKey: ['events'] });
  };

  const filter =
    <T,>(set: (v: T) => void) =>
    (v: T) => {
      set(v);
      setPage(1);
    };

  /* ------------------------------------------------------------- actions --- */

  const add = async () => {
    const loaded = form.data;
    if (!loaded || !addValidation.check()) return;

    setBusy(true);
    setProblem(null);
    try {
      const created = await registrations.createRegistration(event.id, {
        attendeeName: draft.attendeeName.trim(),
        attendeeEmail: draft.attendeeEmail.trim() || undefined,
        attendeePhone: draft.attendeePhone.trim(),
        seatCount: Number(draft.seatCount),
        formVersion: loaded.version,
        answers: answerInputs(loaded, answers),
      });
      await refresh();
      setAdding(false);
      setDraft(blankRegistration);
      setAnswers({});
      const r = created.data;
      setNotice(
        r.status === 'waitlisted'
          ? `${r.attendeeName} is on the waitlist as ${r.reference}: there was no room for ${seatsLabel(r.seatCount)}. Promote them when seats free up.`
          : r.status === 'payment_pending'
            ? `${r.attendeeName} is registered as ${r.reference}. They are confirmed once full payment is recorded.`
            : `${r.attendeeName} is confirmed · ticket ${r.ticket?.code ?? r.reference}. ${
                created.ticketEmailQueued
                  ? 'The QR ticket is on its way by email.'
                  : 'No email was given, so they check in with the ticket code.'
              }`,
      );
    } catch (e) {
      // The form was saved again while this was open: answer the new copy,
      // keeping whatever still fits it.
      if (errorCode(e) === REGISTRATION_FORM_CHANGED) {
        const fresh = await form.refetch();
        if (fresh.data) setAnswers(carryAnswers(loaded, answers, fresh.data));
      }
      setProblem(registrationProblem(e, 'That did not register. Try again.'));
    } finally {
      setBusy(false);
    }
  };

  const pay = async () => {
    if (!open) return;
    if (!payValidation.check()) return;
    setBusy(true);
    setProblem(null);
    try {
      await registrations.recordPayment(event.id, open.id, {
        method: payment.method,
        // The API rejects a typed method on anything but `other`.
        methodDetail: payment.method === 'other' ? payment.methodDetail.trim() : undefined,
        note: payment.note.trim() || undefined,
      });
      await refresh();
      setPaying(false);
      setPayment(blankPayment);
      setNotice(`Payment recorded for ${open.attendeeName}. The QR ticket is on its way by email.`);
    } catch (e) {
      setProblem(registrationProblem(e, 'That did not record. Try again.'));
    } finally {
      setBusy(false);
    }
  };

  const move = async () => {
    if (!open || !moving) return;
    setBusy(true);
    setProblem(null);
    try {
      if (moving === 'promote') {
        const result = await registrations.promoteRegistration(event.id, open.id);
        const r = result.data;
        setNotice(
          r.status === 'payment_pending'
            ? `${r.attendeeName} is off the waitlist and owes ${formatCents(r.payment.balanceAmountMinor)}. Recording the payment confirms them.`
            : `${r.attendeeName} is off the waitlist and confirmed · ticket ${r.ticket?.code ?? r.reference}. ${
                result.ticketEmailQueued
                  ? 'The QR ticket is on its way by email.'
                  : 'No email was given, so they check in with the ticket code.'
              }`,
        );
      } else {
        await registrations.waitlistRegistration(event.id, open.id);
        setNotice(
          `${open.attendeeName} is on the waitlist and can no longer pay until they are promoted.`,
        );
      }
      await refresh();
      setMoving(null);
    } catch (e) {
      setProblem(registrationProblem(e, 'That did not go through. Try again.'));
    } finally {
      setBusy(false);
    }
  };

  const cancel = async () => {
    if (!open) return;
    if (!cancelValidation.check()) return;
    setBusy(true);
    setProblem(null);
    try {
      const result = await registrations.cancelRegistration(event.id, open.id, {
        reason: reason.trim() || undefined,
      });
      await refresh();
      setCancelling(false);
      setReason('');
      setNotice(
        `${open.attendeeName}'s registration is cancelled and the seats are released.${
          result.cancellationEmailQueued ? ' They have been emailed.' : ''
        }`,
      );
    } catch (e) {
      setProblem(registrationProblem(e, 'That did not cancel. Try again.'));
    } finally {
      setBusy(false);
    }
  };

  const cancelAll = async () => {
    if (!cancelValidation.check()) return;
    setBusy(true);
    setProblem(null);
    try {
      const result = await registrations.cancelAllRegistrations(event.id, {
        reason: reason.trim() || undefined,
      });
      await refresh();
      setCancellingAll(false);
      setReason('');
      const parties = (n: number) => `${n} registration${n === 1 ? '' : 's'}`;
      setNotice(
        [
          `${parties(result.cancelledRegistrationCount)} cancelled and ${seatsLabel(result.cancelledSeats)} released.`,
          result.cancellationEmailCount > 0
            ? `${result.cancellationEmailCount} ${result.cancellationEmailCount === 1 ? 'attendee has' : 'attendees have'} been emailed.`
            : '',
          result.checkedInRegistrationCount > 0
            ? `${result.checkedInRegistrationCount} checked-in ${result.checkedInRegistrationCount === 1 ? 'party was' : 'parties were'} not cancelled.`
            : '',
        ]
          .filter(Boolean)
          .join(' '),
      );
    } catch (e) {
      setProblem(registrationProblem(e, 'That did not cancel. Try again.'));
    } finally {
      setBusy(false);
    }
  };

  const toggle = async (r: AdminEventRegistration) => {
    setTogglingId(r.id);
    setNotice(null);
    setProblem(null);
    try {
      const result = await registrations.setAttendance(event.id, r.id, !r.checkedIn);
      await refresh();
      if (result.outcome === 'invalid') setProblem(result.message);
    } catch (e) {
      setProblem(registrationProblem(e, 'Attendance did not change. Try again.'));
    } finally {
      setTogglingId(null);
    }
  };

  const exportXlsx = async () => {
    if (!open) return;
    setExporting(true);
    setExportProblem(null);
    try {
      await exportRegistrationXlsx(open, event.title);
    } catch {
      setExportProblem('The export could not be generated. Try again.');
    } finally {
      setExporting(false);
    }
  };

  const openRow = (r: AdminEventRegistration) => {
    setProblem(null);
    setExportProblem(null);
    setOpenId(r.id);
  };

  /* -------------------------------------------------------------- render --- */

  return (
    <div className="flex flex-col gap-6">
      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-5">
        <StatCard
          label="Seats taken"
          value={`${event.registeredSeats}/${event.capacity}`}
          note={summary ? `${summary.registrationCount} registrations in all` : 'loading'}
          density="compact"
        />
        <StatCard
          label="Payment due"
          value={String(summary?.paymentPendingSeats ?? '–')}
          note={
            summary
              ? `seats across ${summary.paymentPendingRegistrationCount} parties, confirmed once paid`
              : 'loading'
          }
          density="compact"
        />
        <StatCard
          label="Waitlist"
          value={String(summary?.waitlistedSeats ?? '–')}
          note={summary ? `seats across ${summary.waitlistedRegistrationCount} parties` : 'loading'}
          density="compact"
        />
        <StatCard
          label="Checked in"
          value={String(summary?.checkedInSeats ?? '–')}
          note={summary ? `seats across ${summary.checkedInRegistrationCount} parties` : 'loading'}
          density="compact"
        />
        <StatCard
          label="Cancelled"
          value={String(summary?.cancelledSeats ?? '–')}
          note={summary ? `seats across ${summary.cancelledRegistrationCount} parties` : 'loading'}
          density="compact"
        />
      </div>

      {notice && (
        <p
          role="status"
          className="m-0 rounded-xl border border-brand-edge/35 bg-brand-wash px-4 py-3 text-base text-brand-strong"
        >
          {notice}
        </p>
      )}
      {problem &&
        !adding &&
        !paying &&
        !cancelling &&
        !cancellingAll &&
        !moving &&
        openId === null && <Banner>{problem}</Banner>}

      <Card
        title="Registrations"
        action={
          <Toolbar>
            <input
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(1);
              }}
              placeholder="Name, email, phone or MDS-…"
              aria-label="Search registrations"
              className={controlCls + ' w-[240px]'}
            />
            <Select
              value={status}
              onChange={filter((v: string) => setStatus(v as typeof status))}
              options={STATUS_OPTIONS}
              aria-label="Filter by status"
            />
            <Select
              value={attendance}
              onChange={filter((v: string) => setAttendance(v as typeof attendance))}
              options={ATTENDANCE_OPTIONS}
              aria-label="Filter by attendance"
            />
            {mayManage && (
              <Button
                onClick={() => {
                  setDraft(blankRegistration);
                  setAnswers({});
                  setProblem(null);
                  setNotice(null);
                  addValidation.reset();
                  setAdding(true);
                }}
              >
                Add registration
              </Button>
            )}
            {mayManage && cancellableCount > 0 && (
              <Button
                variant="danger"
                onClick={() => {
                  setReason('');
                  setProblem(null);
                  setNotice(null);
                  cancelValidation.reset();
                  setCancellingAll(true);
                }}
              >
                Cancel all
              </Button>
            )}
          </Toolbar>
        }
      >
        {list.isPending ? (
          <p className="m-0 text-base text-ink-muted">Loading registrations…</p>
        ) : list.isError ? (
          <EmptyState
            title="Could not load registrations"
            hint={
              list.error instanceof ApiError
                ? list.error.message
                : 'The server could not be reached.'
            }
          />
        ) : rows.length === 0 ? (
          <EmptyState
            title={summary?.registrationCount ? 'No registration matches' : 'Nobody has registered'}
            hint={
              summary?.registrationCount
                ? 'Try a different search or filter.'
                : mayManage
                  ? 'Registrations taken at the desk or by phone are added here.'
                  : undefined
            }
          />
        ) : (
          <Table head={['Ticket', 'Attendee', 'Seats', 'Amount', 'Status', 'Attendance']}>
            {rows.map((r) => {
              const tone = registrationTone(r);
              return (
                <Row key={r.id} onClick={() => openRow(r)}>
                  <Cell className="font-mono text-md">{r.reference}</Cell>
                  <Cell>
                    <div className="flex flex-col">
                      <span className="font-semibold">{r.attendeeName}</span>
                      <span className="text-md text-ink-muted">
                        {[r.attendeeEmail, r.attendeePhone].filter(Boolean).join(' · ') || '—'}
                      </span>
                    </div>
                  </Cell>
                  <Cell className="font-mono text-md tabular-nums">{r.seatCount}</Cell>
                  <Cell className="font-mono text-md tabular-nums">
                    {eventPriceLabel(r.price.totalAmountMinor)}
                  </Cell>
                  <Cell>
                    <Chip className={'border ' + tone.cls}>{tone.label}</Chip>
                  </Cell>
                  <Cell>
                    {/* Only a confirmed party can be ticked in; the API refuses the rest. */}
                    <label
                      className="flex items-center gap-2 text-base"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <input
                        type="checkbox"
                        className="h-6 w-6 accent-brand-edge"
                        checked={r.checkedIn}
                        disabled={!mayCheckIn || r.status !== 'confirmed' || togglingId !== null}
                        onChange={() => void toggle(r)}
                        aria-label={`${r.attendeeName} checked in`}
                      />
                      <span className="text-ink-muted">{r.checkedIn ? 'In' : 'Not yet'}</span>
                    </label>
                  </Cell>
                </Row>
              );
            })}
          </Table>
        )}
        <Pager pagination={list.data?.pagination} onPage={setPage} />
      </Card>

      {/* ---------------------------------------------------------- detail --- */}
      <Modal
        open={openId !== null && !paying && !cancelling && !moving}
        onClose={() => setOpenId(null)}
        title={open ? `${open.attendeeName} · ${open.reference}` : 'Registration'}
        footer={
          open ? (
            <>
              {mayRecord && open.status === 'payment_pending' && (
                <Button
                  onClick={() => {
                    setPayment(blankPayment);
                    setProblem(null);
                    payValidation.reset();
                    setPaying(true);
                  }}
                >
                  Record full payment
                </Button>
              )}
              {mayManage && open.status === 'waitlisted' && (
                <Button
                  onClick={() => {
                    setProblem(null);
                    setMoving('promote');
                  }}
                >
                  Promote from waitlist
                </Button>
              )}
              {mayManage && open.status === 'payment_pending' && (
                <Button
                  variant="quiet"
                  onClick={() => {
                    setProblem(null);
                    setMoving('waitlist');
                  }}
                >
                  Move to waitlist
                </Button>
              )}
              {mayManage && open.status !== 'cancelled' && !open.checkedIn && (
                <Button
                  variant="danger"
                  onClick={() => {
                    setReason('');
                    setProblem(null);
                    cancelValidation.reset();
                    setCancelling(true);
                  }}
                >
                  Cancel registration
                </Button>
              )}
              <Button
                variant="quiet"
                onClick={() => printDialog(`${open.reference} ${open.attendeeName}`)}
              >
                <PrinterIcon className="size-5" aria-hidden="true" />
                PDF
              </Button>
              <Button variant="quiet" disabled={exporting} onClick={() => void exportXlsx()}>
                <DownloadIcon className="size-5" aria-hidden="true" />
                {exporting ? 'Preparing…' : 'Excel'}
              </Button>
              <Button variant="quiet" onClick={() => setOpenId(null)}>
                Close
              </Button>
            </>
          ) : undefined
        }
      >
        {single.isPending ? (
          <p className="m-0 text-base text-ink-muted">Loading the registration…</p>
        ) : single.isError || !open ? (
          <Banner>
            {single.error instanceof ApiError
              ? single.error.message
              : 'That registration could not be read.'}
          </Banner>
        ) : (
          <>
            {exportProblem && <Banner>{exportProblem}</Banner>}
            <PrintFrame
              title={`${open.attendeeName} · ${open.reference}`}
              subtitle={event.title}
              lines={[`Printed ${stamp(new Date().toISOString())}`]}
            >
              <RegistrationDetail registration={open} />
            </PrintFrame>
          </>
        )}
      </Modal>

      {/* ------------------------------------------------------------- add --- */}
      <Modal
        open={adding}
        onClose={() => !busy && setAdding(false)}
        title="Add a registration"
        footer={
          <>
            <Button onClick={() => void add()} disabled={busy || !form.data}>
              {busy ? 'Registering…' : 'Register'}
            </Button>
            <Button variant="quiet" disabled={busy} onClick={() => setAdding(false)}>
              Cancel
            </Button>
          </>
        }
      >
        {problem && <Banner>{problem}</Banner>}
        <p className="m-0 text-base">
          {priced
            ? `${eventPriceLabel(event.price.amountMinor)} a seat. The registration is confirmed and ticketed once full payment is recorded.`
            : 'Free. The registration is confirmed and the ticket issued straight away.'}{' '}
          A party with no room joins the waitlist instead.
        </p>
        <Field label="Name" required {...addValidation.field('attendeeName')}>
          <input
            className={inputCls}
            value={draft.attendeeName}
            maxLength={160}
            onChange={(e) => setDraft({ ...draft, attendeeName: e.target.value })}
          />
        </Field>
        <div className="grid gap-4 sm:grid-cols-2">
          <Field
            label="Email"
            hint={priced ? 'Required, since the QR ticket is emailed.' : 'Optional, for the QR ticket.'}
            required={priced}
            {...addValidation.field('attendeeEmail')}
          >
            <input
              type="email"
              className={inputCls}
              value={draft.attendeeEmail}
              maxLength={254}
              onChange={(e) => setDraft({ ...draft, attendeeEmail: e.target.value })}
            />
          </Field>
          <Field
            label="Phone"
            hint="Required, to reach them if they are waitlisted or have not paid."
            required
            {...addValidation.field('attendeePhone')}
          >
            <input
              type="tel"
              className={inputCls}
              value={draft.attendeePhone}
              maxLength={50}
              onChange={(e) => setDraft({ ...draft, attendeePhone: e.target.value })}
            />
          </Field>
        </div>
        <Field
          label="Seats"
          hint={
            arrivesWaitlisted(event, Number(draft.seatCount))
              ? `Only ${seatsLabel(seatsFree(event))} free, so this party goes on the waitlist.`
              : priced && Number(draft.seatCount) > 0
                ? `Total ${formatCents(event.price.amountMinor * Number(draft.seatCount))}`
                : 'One ticket covers the whole party.'
          }
          required
          {...addValidation.field('seatCount')}
        >
          <input
            type="number"
            min={1}
            className={inputCls}
            value={draft.seatCount}
            onChange={(e) => setDraft({ ...draft, seatCount: e.target.value })}
          />
        </Field>
        {form.isPending ? (
          <p className="m-0 text-base text-ink-muted">Loading the registration questions…</p>
        ) : form.isError ? (
          <Banner>
            The registration questions could not be loaded, so this party cannot be added yet.
          </Banner>
        ) : (
          <RegistrationQuestions
            form={form.data}
            answers={answers}
            onAnswer={(id, value) => setAnswers((current) => ({ ...current, [id]: value }))}
            field={addValidation.field}
          />
        )}
      </Modal>

      {/* --------------------------------------------------------- payment --- */}
      <Modal
        open={paying && open !== null}
        onClose={() => !busy && setPaying(false)}
        title="Record full payment"
        footer={
          <>
            <Button onClick={() => void pay()} disabled={busy}>
              {busy ? 'Recording…' : 'Record payment'}
            </Button>
            <Button variant="quiet" disabled={busy} onClick={() => setPaying(false)}>
              Back
            </Button>
          </>
        }
      >
        {problem && <Banner>{problem}</Banner>}
        {open && (
          <>
            {/* Events take full payment only, so the amount is read, never typed. */}
            <div className="flex items-center justify-between rounded-xl border border-hairline bg-raised px-4 py-3">
              <span className="text-base text-ink-muted">
                {open.attendeeName} · {seatsLabel(open.seatCount)}
              </span>
              <span className="text-xl font-semibold tabular-nums">
                {formatCents(open.payment.balanceAmountMinor)}
              </span>
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="Method" required {...payValidation.field('method')}>
                <Select
                  variant="field"
                  className={selectCls}
                  value={payment.method}
                  onChange={(method) =>
                    setPayment({ ...payment, method: method as EventPaymentMethod })
                  }
                  options={EVENT_PAYMENT_METHOD_OPTIONS}
                  aria-label="Method"
                />
              </Field>
              {payment.method === 'other' && (
                <Field
                  label="What was it"
                  hint="Required: cheque, bank transfer, and so on."
                  required
                  {...payValidation.field('methodDetail')}
                >
                  <input
                    className={inputCls}
                    value={payment.methodDetail}
                    maxLength={80}
                    onChange={(e) => setPayment({ ...payment, methodDetail: e.target.value })}
                  />
                </Field>
              )}
            </div>
            <Field
              label="Note"
              hint="Optional. Kept on the payment record, which cannot be edited."
              {...payValidation.field('note')}
            >
              <input
                className={inputCls}
                value={payment.note}
                maxLength={500}
                onChange={(e) => setPayment({ ...payment, note: e.target.value })}
              />
            </Field>
            <p className="m-0 text-base text-ink-muted">
              Recording confirms the registration and emails the QR ticket, even if that takes the
              event over capacity. Event payments are not refundable.
            </p>
          </>
        )}
      </Modal>

      {/* -------------------------------------------------------- waitlist --- */}
      <Modal
        open={moving !== null && open !== null}
        onClose={() => !busy && setMoving(null)}
        title={moving === 'promote' ? 'Promote from the waitlist' : 'Move to the waitlist'}
        footer={
          <>
            <Button
              onClick={() => void move()}
              // A free party is confirmed on promotion, so it cannot go without the room.
              disabled={
                busy ||
                (moving === 'promote' &&
                  open !== null &&
                  open.price.totalAmountMinor === 0 &&
                  seatsFree(event) < open.seatCount)
              }
            >
              {busy ? 'Moving…' : moving === 'promote' ? 'Promote' : 'Move to waitlist'}
            </Button>
            <Button variant="quiet" disabled={busy} onClick={() => setMoving(null)}>
              Back
            </Button>
          </>
        }
      >
        {problem && <Banner>{problem}</Banner>}
        {open && moving === 'promote' && (
          <p className="m-0 text-base">
            {open.price.totalAmountMinor === 0
              ? seatsFree(event) >= open.seatCount
                ? `${open.attendeeName}'s ${seatsLabel(open.seatCount)} are confirmed and the QR ticket is issued.`
                : `Only ${seatsLabel(seatsFree(event))} free, and ${open.attendeeName} needs ${open.seatCount}. Cancel a registration or raise the capacity first.`
              : `${open.attendeeName} moves to payment due and owes ${formatCents(open.price.totalAmountMinor)}. Recording the payment confirms them${
                  seatsFree(event) < open.seatCount
                    ? `. The event is full, so it will go ${open.seatCount - seatsFree(event)} over capacity`
                    : ''
                }.`}
          </p>
        )}
        {open && moving === 'waitlist' && (
          <p className="m-0 text-base">
            {open.attendeeName}&rsquo;s {seatsLabel(open.seatCount)} come off payment due and go on
            the waitlist. Until they are promoted again, no payment can be recorded for them. Use
            this to stop a full event filling further.
          </p>
        )}
      </Modal>

      {/* ---------------------------------------------------------- cancel --- */}
      <Modal
        open={cancelling && open !== null}
        onClose={() => !busy && setCancelling(false)}
        title="Cancel this registration"
        footer={
          <>
            <Button variant="danger" onClick={() => void cancel()} disabled={busy}>
              {busy ? 'Cancelling…' : 'Cancel registration'}
            </Button>
            <Button variant="quiet" disabled={busy} onClick={() => setCancelling(false)}>
              Keep it
            </Button>
          </>
        }
      >
        {problem && <Banner>{problem}</Banner>}
        {open && (
          <p className="m-0 text-base">
            {open.attendeeName}&rsquo;s {seatsLabel(open.seatCount)} go back on sale
            {open.ticket ? ' and ticket ' + open.ticket.code + ' stops working' : ''}.
            {open.payment.status === 'paid'
              ? ' The payment stays on record and is not refunded.'
              : ''}
          </p>
        )}
        <Field
          label="Reason"
          hint="Optional. Kept with the registration."
          {...cancelValidation.field('reason')}
        >
          <input
            className={inputCls}
            value={reason}
            maxLength={500}
            placeholder="Called the MDS office"
            onChange={(e) => setReason(e.target.value)}
          />
        </Field>
      </Modal>

      {/* ------------------------------------------------------ cancel all --- */}
      <Modal
        open={cancellingAll}
        onClose={() => !busy && setCancellingAll(false)}
        title="Cancel every registration"
        footer={
          <>
            <Button variant="danger" onClick={() => void cancelAll()} disabled={busy}>
              {busy
                ? 'Cancelling…'
                : `Cancel ${cancellableCount} registration${cancellableCount === 1 ? '' : 's'}`}
            </Button>
            <Button variant="quiet" disabled={busy} onClick={() => setCancellingAll(false)}>
              Keep them
            </Button>
          </>
        }
      >
        {problem && <Banner>{problem}</Banner>}
        <p className="m-0 text-base">
          Every registration that is not already cancelled is cancelled, waitlisted and unpaid ones
          included. Their seats go back on sale, their tickets stop working, and any payment stays
          on record without a refund. Each attendee with an email address is told.
          {summary && summary.checkedInRegistrationCount > 0
            ? ` ${summary.checkedInRegistrationCount} checked-in ${summary.checkedInRegistrationCount === 1 ? 'party is' : 'parties are'} not cancelled.`
            : ''}{' '}
          This cannot be undone.
        </p>
        <Field
          label="Reason"
          hint="Optional. Kept with each registration and included in the email."
          {...cancelValidation.field('reason')}
        >
          <input
            className={inputCls}
            value={reason}
            maxLength={500}
            placeholder="The event is postponed"
            onChange={(e) => setReason(e.target.value)}
          />
        </Field>
      </Modal>
    </div>
  );
}

function RegistrationDetail({ registration: r }: { registration: AdminEventRegistration }) {
  const tone = registrationTone(r);
  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap gap-2">
        <Chip className={'border ' + tone.cls}>{tone.label}</Chip>
        <Chip>{seatsLabel(r.seatCount)}</Chip>
        <Chip>{eventPriceLabel(r.price.totalAmountMinor)}</Chip>
        {r.ticket && <Chip>Ticket {r.ticket.code}</Chip>}
      </div>

      <dl className="m-0 grid grid-cols-[auto_1fr] gap-x-5 gap-y-3 text-base">
        {registrationFacts(r).map((f) => (
          <Line key={f.label} label={f.label}>
            {f.value}
          </Line>
        ))}
      </dl>

      {r.answers.length > 0 && (
        <dl className="m-0 flex flex-col gap-3 border-t border-hairline pt-3 text-base">
          {r.answers.map(answerLine).map((a, i) => (
            <div key={i}>
              <dt className="font-mono text-sm text-ink-muted uppercase">{a.question}</dt>
              <dd className="m-0 mt-1 break-words">{a.answer}</dd>
            </div>
          ))}
        </dl>
      )}
    </div>
  );
}

/**
 * The event's registration questions in the add dialog, section by section,
 * exactly as the public page asks them.
 */
function RegistrationQuestions({
  form,
  answers,
  onAnswer,
  field,
}: {
  form: FormQuestionSet;
  answers: AnswerDrafts;
  onAnswer: (questionId: string, value: AnswerDraft) => void;
  field: FieldBinder;
}) {
  if (questionsOf(form).length === 0) return null;
  const ask = (q: FormQuestion) => (
    <QuestionField
      key={q.id}
      question={q}
      value={answers[q.id] ?? blankAnswer(q)}
      onChange={(value) => onAnswer(q.id, value)}
      {...field(`answers.${q.id}`)}
    />
  );
  return (
    <>
      {form.ungroupedQuestions.map(ask)}
      {form.sections.map((section) => (
        <div key={section.id} className="flex flex-col gap-4 border-t border-hairline pt-4">
          <div>
            <p className="m-0 text-base font-semibold">{section.title}</p>
            {section.description && (
              <p className="m-0 mt-1 text-base text-ink-muted">{section.description}</p>
            )}
          </div>
          {section.questions.map(ask)}
        </div>
      ))}
    </>
  );
}

function Line({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <>
      <dt className="font-mono text-sm text-ink-muted uppercase">{label}</dt>
      <dd className="m-0 break-words">{children}</dd>
    </>
  );
}

function Banner({ children }: { children: React.ReactNode }) {
  return (
    <p
      role="alert"
      className="m-0 rounded-xl border border-danger-edge bg-danger-wash px-4 py-3 text-base text-danger-text"
    >
      {children}
    </p>
  );
}
